/** SERIALS inline: universal barcode scanner (USB/Bluetooth wedge + Camera + Enter adds),
 *  duplicate detection, audio feedback, type, paste a column, allocate from inventory. */
import { useCallback, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, Camera, CheckCircle2, AlertTriangle, X, Volume2, VolumeX } from "lucide-react";
import { CameraButton } from "@/components/warehouse/CameraScan";
import { useWedgeScanner } from "@/hooks/useWedgeScanner";
import { OpsBlock } from "./OpsTop";

const err = (e) => e.response?.data?.detail || e.message;
const split = (s) => String(s || "").split(/[\s,;/]+/).map((x) => x.trim().toUpperCase()).filter(Boolean);

function playBeep(success = true) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const t = ctx.currentTime;
    if (success) {
      // High-pitch double chirp: 880Hz -> 1320Hz
      const o1 = ctx.createOscillator();
      const g1 = ctx.createGain();
      o1.type = "sine";
      o1.frequency.value = 880;
      o1.connect(g1);
      g1.connect(ctx.destination);
      g1.gain.setValueAtTime(0.12, t);
      g1.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
      o1.start(t);
      o1.stop(t + 0.09);

      const o2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      o2.type = "sine";
      o2.frequency.value = 1320;
      o2.connect(g2);
      g2.connect(ctx.destination);
      g2.gain.setValueAtTime(0.12, t + 0.09);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      o2.start(t + 0.09);
      o2.stop(t + 0.19);
    } else {
      // Low buzz for duplicate / error: 160Hz sawtooth
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sawtooth";
      o.frequency.value = 160;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.15, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
      o.start(t);
      o.stop(t + 0.29);
    }
    setTimeout(() => ctx.close(), 500);
  } catch {
    /* audio unavailable */
  }
}

export function SerialsSection({ qid, st, reload }) {
  const [showOptional, setShowOptional] = useState(() => st.rows.some((r) => !r.serialised && r.serials.length));
  const rows = st.rows.filter((r) => r.serialised || r.serials.length || showOptional);
  const optionalCount = st.rows.filter((r) => !r.serialised && !r.serials.length).length;
  const [v, setV] = useState(() => Object.fromEntries(st.rows.map((r) => [r.allocation_id, r.serials.join(", ")])));
  const [scan, setScan] = useState("");
  const [busy, setBusy] = useState(false);
  const [targetId, setTargetId] = useState(null); // optional explicit row selection
  const [sound, setSound] = useState(true);

  const need = rows.filter((r) => r.serialised).reduce((s, r) => s + Math.max(0, Number(r.qty || 0) - split(v[r.allocation_id]).length), 0);
  const optionalOpen = rows.some((r) => !r.serialised && split(v[r.allocation_id]).length < Number(r.qty || 0));

  const handleScannedCode = useCallback((rawCode, explicitAllocId = null) => {
    const code = String(rawCode || "").trim().toUpperCase();
    if (!code) return;

    // 1. Check for cross-row duplicate in the current order
    const existing = rows.find((r) => split(v[r.allocation_id]).includes(code));
    if (existing) {
      if (sound) playBeep(false);
      toast.error(`Serial "${code}" is already assigned to ${existing.recipient_name}!`);
      return;
    }

    // 2. Identify target row (explicit row or first row needing serials)
    let target = null;
    if (explicitAllocId) {
      const explicitRow = rows.find((r) => r.allocation_id === explicitAllocId);
      if (explicitRow && split(v[explicitRow.allocation_id]).length < Number(explicitRow.qty || 0)) {
        target = explicitRow;
      }
    }
    if (!target && targetId) {
      const activeRow = rows.find((r) => r.allocation_id === targetId);
      if (activeRow && split(v[activeRow.allocation_id]).length < Number(activeRow.qty || 0)) {
        target = activeRow;
      }
    }
    if (!target) {
      target = rows.find((r) => split(v[r.allocation_id]).length < Number(r.qty || 0));
    }

    if (!target) {
      if (sound) playBeep(false);
      toast.error("Every unit on this order already has all required serials assigned.");
      return;
    }

    const cur = split(v[target.allocation_id]);
    const nextSerials = [...cur, code];
    setV((prev) => ({ ...prev, [target.allocation_id]: nextSerials.join(", ") }));
    if (sound) playBeep(true);
    toast.success(`Scanned ${code} → ${target.recipient_name} (${nextSerials.length}/${Number(target.qty || 0)})`);
  }, [rows, v, sound, targetId]);

  // Global USB / Bluetooth wedge scanner listener
  const { lastSource } = useWedgeScanner((code) => {
    handleScannedCode(code);
  }, { enabled: need > 0 || optionalOpen });

  if (!st.rows.length) return null;

  const onScanInputKey = (e) => {
    if (e.key !== "Enter" || !scan.trim()) return;
    e.preventDefault();
    handleScannedCode(scan);
    setScan("");
  };

  const removeSerial = (allocId, serialToRemove) => {
    const list = split(v[allocId]).filter((s) => s !== serialToRemove);
    setV((prev) => ({ ...prev, [allocId]: list.join(", ") }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/quote-operations/quotes/${qid}/serials`, rows.map((r) => ({ allocation_id: r.allocation_id, serials: split(v[r.allocation_id]) })));
      toast.success("Serials saved & mapped");
      reload();
    } catch (e) {
      toast.error(err(e));
    }
    setBusy(false);
  };

  const alloc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quote-operations/quotes/${qid}/serials/allocate`);
      toast.success(`${data.assigned} serial(s) allocated from inventory${data.short.length ? ` · ${data.short.length} short` : ""}`);
      reload();
    } catch (e) {
      toast.error(err(e));
    }
    setBusy(false);
  };

  const pasteCol = (txt) => {
    const list = split(txt);
    const nv = { ...v };
    for (const r of rows) {
      const have = split(nv[r.allocation_id]);
      while (have.length < Number(r.qty || 0) && list.length) {
        const nextSerial = list.shift();
        if (!have.includes(nextSerial)) have.push(nextSerial);
      }
      nv[r.allocation_id] = have.join(", ");
    }
    setV(nv);
  };

  return (
    <OpsBlock id="ops-serials" title="Device / serial allocation" done={need === 0}
      right={rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="relative flex items-center">
            <ScanLine className="absolute left-2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <input
              value={scan}
              onChange={(e) => setScan(e.target.value)}
              onKeyDown={onScanInputKey}
              placeholder="Scan OEM barcode ⏎"
              className="h-7 w-44 rounded-md border border-slate-300 pl-7 pr-2 font-mono text-[11px] focus:border-slate-800 focus:outline-none"
              data-testid="ops-serial-scan"
            />
          </div>

          <CameraButton
            onCode={(c) => handleScannedCode(c)}
            className="h-7 !px-2.5 !border-slate-300 !text-slate-700 hover:!bg-slate-100"
            tid="ops-serial-camera-btn"
          />

          <button
            type="button"
            onClick={() => setSound((s) => !s)}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100"
            title={sound ? "Scanner audio enabled (click to mute)" : "Scanner audio muted (click to enable)"}
          >
            {sound ? <Volume2 className="h-3.5 w-3.5 text-emerald-600" /> : <VolumeX className="h-3.5 w-3.5 text-slate-400" />}
          </button>

          {lastSource && (
            <span className="hidden sm:inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-semibold text-slate-600">
              <CheckCircle2 className="h-2.5 w-2.5 text-emerald-600" />
              {lastSource === "wedge" ? "USB Scanner" : "Camera"}
            </span>
          )}

          <button
            type="button"
            onClick={() => { const t = window.prompt("Paste serial numbers (one per line or comma separated) — filled in recipient order"); if (t) pasteCol(t); }}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-semibold hover:bg-slate-50"
            data-testid="ops-serial-paste"
          >
            Paste / import
          </button>

          <button
            type="button"
            onClick={alloc}
            disabled={busy}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50 hover:bg-slate-50"
            data-testid="ops-serial-allocate"
          >
            ALLOCATE FROM INVENTORY
          </button>

          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="rounded-md bg-slate-900 px-3 py-1 text-[11px] font-bold text-white disabled:opacity-50 hover:bg-black"
            data-testid="ops-serial-save"
          >
            Save serials
          </button>
        </div>
      )}>
      {optionalCount > 0 && !showOptional && (
        <div className={`flex flex-wrap items-center gap-2 text-[12px] text-slate-500 ${rows.length ? "mb-2" : ""}`} data-testid="ops-serial-none">
          <span>{rows.length ? `${optionalCount} more item(s) don't need serials.` : "No serialised device on this order — serials are optional."}</span>
          <button type="button" onClick={() => setShowOptional(true)} className="rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50" data-testid="ops-serial-show-optional">
            + Add serial numbers for reference
          </button>
        </div>
      )}
      {rows.length > 0 && (
        <table className="w-full text-[11.5px]" data-testid="ops-serial-grid">
          <thead>
            <tr className="text-left text-[9.5px] uppercase tracking-wider text-slate-400">
              <th className="py-1">Employee / Recipient</th>
              <th>Product</th>
              <th>Part No.</th>
              <th className="w-16">Progress</th>
              <th>Allocated Serial(s)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const allocatedList = split(v[r.allocation_id]);
              const have = allocatedList.length;
              const maxQty = Number(r.qty || 0);
              const isLocked = ["DISPATCHED", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED"].includes(r.state);
              const isTarget = targetId === r.allocation_id;
              const isComplete = have >= maxQty;

              return (
                <tr
                  key={r.allocation_id}
                  className={`border-t border-slate-100 align-top transition-colors ${isTarget ? "bg-amber-50/50" : ""}`}
                  data-testid={`ops-serial-row-${r.allocation_id}`}
                >
                  <td className="py-2 pr-2 font-semibold">
                    <div>{r.recipient_name}</div>
                    {!isLocked && !isComplete && (
                      <button
                        type="button"
                        onClick={() => setTargetId(isTarget ? null : r.allocation_id)}
                        className={`mt-1 inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider ${isTarget ? "text-amber-800 underline" : "text-slate-400 hover:text-slate-700"}`}
                      >
                        {isTarget ? "● Active Scan Target" : "Select as target"}
                      </button>
                    )}
                  </td>
                  <td className="max-w-[240px] pr-2 text-slate-600">{r.product}
                    {!r.serialised && <div className="mt-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-slate-400" data-testid={`ops-serial-optional-${r.allocation_id}`}>Optional · for reference</div>}</td>
                  <td className="pr-2 font-mono text-[10.5px]">{r.sku || "—"}</td>
                  <td className="pr-2 font-semibold">
                    <span className={isComplete ? "text-emerald-700 font-bold" : r.serialised ? "text-amber-700" : "text-slate-500"}>
                      {have}/{String(r.qty).replace(/\.0$/, "")}
                    </span>
                  </td>
                  <td className="py-1">
                    <div className="space-y-1.5">
                      {allocatedList.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {allocatedList.map((sn) => (
                            <span
                              key={sn}
                              className="inline-flex items-center gap-1 rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 font-mono text-[10.5px] text-slate-800"
                            >
                              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                              {sn}
                              {!isLocked && (
                                <button
                                  type="button"
                                  onClick={() => removeSerial(r.allocation_id, sn)}
                                  className="ml-0.5 text-slate-400 hover:text-rose-600"
                                  title="Remove serial"
                                >
                                  ×
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}

                      {!isLocked && (
                        <div className="flex items-center gap-1.5">
                          <input
                            value={v[r.allocation_id] || ""}
                            onChange={(e) => setV({ ...v, [r.allocation_id]: e.target.value })}
                            placeholder="Type or comma-separate serials"
                            className="h-7 w-full rounded border border-slate-300 px-2 font-mono text-[11px] focus:border-slate-800 focus:outline-none"
                            data-testid={`ops-serial-input-${r.allocation_id}`}
                          />
                          {!isComplete && (
                            <CameraButton
                              onCode={(c) => handleScannedCode(c, r.allocation_id)}
                              className="h-7 !px-2 shrink-0 !border-slate-200 text-slate-600 hover:!bg-slate-100"
                              tid={`camera-row-${r.allocation_id}`}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </OpsBlock>
  );
}
